/** Recoverable saga. Every durable write uses the owner's encrypted datastore CAS. */
import { fullCaseFold } from "./project-casefold";
import {
  isSupportedProjectCharacter,
  stripProjectWhitespace,
  hasValidProjectSurrogates,
} from "./project-unicode-domain";

export const JOURNAL = "_code10_projects_v1";

export function validateName(value) {
  if (!hasValidProjectSurrogates(value))
    throw new Error("PROJECT_NAME_INVALID");
  const stripped = stripProjectWhitespace(value);
  if (!Array.from(stripped).every(isSupportedProjectCharacter))
    throw new Error("PROJECT_NAME_INVALID");
  const name = stripped.normalize("NFC");
  if (
    !name ||
    [...name].length > 56 ||
    !Array.from(name).every(isSupportedProjectCharacter)
  ) {
    throw new Error("PROJECT_NAME_INVALID");
  }
  return name;
}

export const nameKey = (name) => fullCaseFold(name.normalize("NFKC"));

export function ownedProject(response, node, datastoreId, rootId) {
  if (response.contract !== 1 || !Array.isArray(response.roots))
    throw new Error("PROJECT_CONTRACT_UNSUPPORTED");
  const root = response.roots.find(
    (item) => item.datastore_id === datastoreId && item.folder_id === rootId
  );
  return Boolean(
    root?.projects.some(
      (project) =>
        project.folder_id === node.id && project.share_id === node.share_id
    )
  );
}

export function protectedMove(
  sourceNode,
  sourcePath,
  targetPath,
  type,
  rootId
) {
  if (!rootId || type !== "folders") return false;
  return (
    sourcePath[sourcePath.length - 1] === rootId ||
    targetPath?.[targetPath.length - 1] === rootId ||
    sourcePath[sourcePath.length - 2] === rootId ||
    Boolean(findRoot(sourceNode, rootId))
  );
}

export function findRoot(tree, id) {
  if (tree.id === id) return tree;
  for (const folder of tree.folders || []) {
    if (folder.share_id) continue;
    const found = findRoot(folder, id);
    if (found) return found;
  }
  return null;
}

export function verifyResult(result, operation) {
  const flags = {
    Read: [true, false, false],
    Write: [true, true, false],
    Admin: [true, true, true],
  };
  if (
    result?.contract !== 1 ||
    result.project_id !== operation.payload.project_id ||
    result.operation_id !== operation.payload.operation_id ||
    result.folder_id !== operation.payload.folder_id ||
    !result.share_id ||
    Object.keys(result.roles || {}).length !== 3
  ) {
    throw new Error("PROJECT_RESULT_INVALID");
  }
  const ids = new Set();
  for (const [role, expected] of Object.entries(flags)) {
    const grant = result.roles[role];
    if (
      !grant?.group_id ||
      !grant.grant_id ||
      grant.name !== result.name + " - " + role ||
      [grant.read, grant.write, grant.grant].some(
        (value, i) => value !== expected[i]
      )
    ) {
      throw new Error("PROJECT_GRANTS_INVALID");
    }
    ids.add(grant.group_id);
  }
  if (ids.size !== 3) throw new Error("PROJECT_GRANTS_INVALID");
}

export async function change(adapter, mutate) {
  for (let attempt = 0; attempt < 3; attempt++) {
    adapter.current();
    const tree = await adapter.read();
    adapter.current();
    const result = mutate(tree);
    if (!result.write) return result.value;
    try {
      await adapter.write(tree);
      adapter.current();
      return result.value;
    } catch (error) {
      if (error.code !== "PROJECT_VAULT_REVISION_CONFLICT") throw error;
    }
  }
  throw new Error("PROJECT_VAULT_REVISION_CONFLICT");
}

export async function createProject(
  adapter,
  root,
  displayName,
  color,
  resumeId
) {
  const name = validateName(displayName);
  let prepared;
  const operation = await change(adapter, (tree) => {
    const parent = findRoot(tree, root.folder_id);
    if (!parent) throw new Error("PROJECT_PARENT_INVALID");
    const journal = (tree[JOURNAL] ||= { operations: {}, projects: {} });
    let pending = resumeId
      ? journal.operations[resumeId]
      : Object.values(journal.operations).find(
          (op) => nameKey(op.payload.name) === nameKey(name)
        );
    if (resumeId && !pending) throw new Error("PROJECT_OPERATION_NOT_FOUND");
    if (pending) return { write: false, value: pending };
    if (
      (parent.folders || []).some(
        (folder) => nameKey(folder.name) === nameKey(name)
      )
    ) {
      throw new Error("PROJECT_COLLISION");
    }
    prepared ||= adapter.prepare(root, name, color);
    journal.operations[prepared.payload.operation_id] = prepared;
    return { write: true, value: prepared };
  });
  adapter.current();
  const created = await adapter.provision(operation.payload);
  adapter.current();
  verifyResult(created, operation);
  const persisted = await adapter.verify(operation.payload.project_id);
  adapter.current();
  verifyResult(persisted, operation);
  if (persisted.share_id !== created.share_id)
    throw new Error("PROJECT_RESULT_INVALID");
  await change(adapter, (tree) => {
    const parent = findRoot(tree, root.folder_id);
    if (!parent) throw new Error("PROJECT_PARENT_INVALID");
    parent.folders ||= [];
    const old = parent.folders.find(
      (folder) => folder.id === persisted.folder_id
    );
    if (
      old &&
      (old.share_id !== persisted.share_id ||
        old.share_secret_key !== operation.shareKey)
    ) {
      throw new Error("PROJECT_FOLDER_CONFLICT");
    }
    if (!old) {
      if (
        parent.folders.some(
          (folder) => nameKey(folder.name) === nameKey(operation.payload.name)
        )
      ) {
        throw new Error("PROJECT_COLLISION");
      }
      parent.folders.push({
        id: persisted.folder_id,
        name: operation.payload.name,
        color: operation.color,
        share_id: persisted.share_id,
        share_secret_key: operation.shareKey,
      });
    }
    const journal = tree[JOURNAL];
    journal.projects[persisted.folder_id] = {
      project_id: persisted.project_id,
      revision: persisted.revision,
    };
    journal.operations[operation.payload.operation_id].state = "complete";
    return { write: true, value: persisted };
  });
  // A timeout after the vault committed is recovered by this independent reread.
  const saved = await adapter.read();
  adapter.current();
  const reference = findRoot(saved, root.folder_id)?.folders?.find(
    (folder) => folder.id === persisted.folder_id
  );
  if (
    reference?.share_id !== persisted.share_id ||
    reference.share_secret_key !== operation.shareKey ||
    saved[JOURNAL]?.operations?.[operation.payload.operation_id]?.state !==
      "complete"
  )
    throw new Error("PROJECT_COMMIT_UNVERIFIED");
  return persisted;
}
